import {finalize, takeUntil} from 'rxjs/operators';
import {Component, forwardRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {Metadata, MetadataId} from '@app/graphql-typings/graphql';
import {ControlValueAccessor, NG_VALUE_ACCESSOR, FormControl} from '@angular/forms';
import {MetadataService} from "@shared/services/api/metadata.service";
import {noop, Subject, Subscription} from 'rxjs';
import {ArrayUtils} from '@shared/util/collections/collection-utils';
import {AppConstants} from '@shared/util/app-constants';

type MetadataSelectValue = Metadata | Metadata[] | MetadataId | MetadataId[] | null;

@Component({
  selector: 'app-metadata-select',
  templateUrl: './metadata-select.component.html',
  styleUrls: ['./metadata-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MetadataSelectComponent),
      multi: true
    }
  ],
  standalone: false
})
export class MetadataSelectComponent implements OnInit, OnChanges, OnDestroy, ControlValueAccessor {
  @Input() metadata: Metadata[];
  @Input() loading = false;
  @Input() id: string;
  @Input() class: string;
  /* selectCommissionable can have three states;
  * - null - All metadata (commissionable & non-commissionable) will be shown
  * - true - Only commissionable metadata will be shown
  * - false - Only non-commissionable metadata will be shown */
  @Input() selectCommissionable: boolean = null;
  @Input() bindToMetadataId: boolean = false;
  @Input() bindLabel: string;
  @Input() multiSelect: boolean = false;
  @Input() groupBy: string | Function;
  @Input() maxSelectedLabels: number;
  @Input() showClearItem = true;
  @Input() truncateLabels = false;
  @Input() virtualScroll = false;
  @Input() appendTo = AppConstants.DROPDOWN_HOLDER_SELECTOR;

  readonly METADATA_ID_KEY = 'metadata_id';

  private readonly ngUnsubscribe = new Subject<void>();

  private fetchMetadataSubscription: Subscription;
  private onChange: (value: MetadataSelectValue) => void = noop;

  onTouched: () => void = noop;

  formControl: FormControl<MetadataSelectValue> = new FormControl<Metadata | null>(null);

  constructor(private metadataService: MetadataService) {}

  ngOnInit(): void {
    this.formControl.valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value: MetadataSelectValue) => this.onChange(value));
    if (this.metadata == null) {
      this.fetchMetadata();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.metadata) {
      if (this.metadata) {
        if (!changes.loading) {
          this.loading = false;
        }
        this.cancelSubscription();
      } else {
        this.fetchMetadata();
      }
    }
  }

  private fetchMetadata(): void {
    if (this.fetchMetadataSubscription == null) {
      this.loading = true;
      this.fetchMetadataSubscription = this.metadataService.getAllMetadata({commissionable: this.selectCommissionable})
        .pipe(finalize(() => {
            this.loading = false;
            this.cancelSubscription();
          }
        ))
        .subscribe(metadata => this.metadata = metadata);
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.cancelSubscription();
  }

  private cancelSubscription(): void {
    if (this.fetchMetadataSubscription != null) {
      this.fetchMetadataSubscription.unsubscribe();
      this.fetchMetadataSubscription = null;
    }
  }

  registerOnChange(fn: (value: MetadataSelectValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.formControl.disable({emitEvent: false});
    } else {
      this.formControl.enable({emitEvent: false});
    }
  }

  private getId(item: Metadata | MetadataId): MetadataId {
    // Always returns a MetadataId for the given item, whether it's a Metadata or MetadataId.
    return item != null && typeof item === 'object' && this.METADATA_ID_KEY in item
      ? item.metadata_id
      : (<MetadataId>item ?? null);
  }

  writeValue(value: MetadataSelectValue): void {
    let valueChanged: boolean;
    if (this.multiSelect) {
      const arr1: MetadataId[] = (<Metadata[] | MetadataId[]>this.formControl.value ?? []).map(item => this.getId(item));
      const arr2: MetadataId[] = (<Metadata[] | MetadataId[]>value ?? []).map(item => this.getId(item));
      valueChanged = !ArrayUtils.containSameItems(arr1, arr2);
    } else {
      valueChanged = this.getId(<Metadata | MetadataId>this.formControl.value) !== this.getId(<Metadata | MetadataId>value);
    }
    if (valueChanged) {
      this.formControl.setValue(value, {emitEvent: false});
    }
  }

  searchFn = (term: string, metadata: Metadata): boolean => {
    const t = term.toLocaleLowerCase();
    return metadata.name.toLocaleLowerCase().includes(t) ||
      metadata.code.toLocaleLowerCase().includes(t) ||
      metadata.unit.unit.toLocaleLowerCase().includes(t);
  };
}
